import { FC, useEffect, useState } from 'react';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { v4 as uuidv4 } from 'uuid';
import { FormType } from '../../common';
import { useFormContext, useWatch } from 'react-hook-form';
import { PackagingTypes, ShrimpBaseline } from '../../../../models/Baseline/ShrimpBaseline';
import ReactHookDsmInput from '../../../../../modules/Helpers/ReactHookDsmInput2';
import ReactHookDsmSelect from '../../../../../modules/Helpers/ReactHookDsmSelect2';
import { enumToOptionsArrayWithTranslatedStrings } from '../../../../utils/obj-utils';
import { PrimaryPackagingType, SecondaryPackagingType, TertiaryPackagingType } from '../../../../../../graphql/generated/blonk/shrimp';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import { OutputOption } from './ShrimpPackagingCategoryComponent';
import lookupValues from '../../../../helpers/lookupValues';

interface ShrimpPackagingTypeComponentProps {
    formType: FormType;
    fieldItemPrefix: string;
    packagingType: string;
    unavailableOptions: OutputOption[];
}

const ShrimpPackagingTypeComponent: FC<ShrimpPackagingTypeComponentProps> = ({
    formType,
    fieldItemPrefix,
    unavailableOptions,
    packagingType = "primary"
}) => {
    const intl = useIntl();
    const fc = useFormContext<ShrimpBaseline>();
    const userProfile = UserProfilePrefs.getInstance();
    const userUOM = userProfile.getUserUnitPrefs();
    const [disablePackagingTypeField, setDisablePackagingTypeField] = useState<boolean>(false);
    const [disableAmountField, setDisableAmountField] = useState<boolean>(false);

    const getPackagingLabelId = (type: string): string => {
        switch (type) {
            case "primary":
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.PRIMARY_PACKAGING_TYPE";
            case "secondary":
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.SECONDARY_PACKAGING_TYPE";
            case "tertiary":
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.TERTIARY_PACKAGING_TYPE";
            default:
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.PRIMARY_PACKAGING_TYPE";
        }
    };

    const getPackagingTooltipId = (type: string): string => {
        switch (type) {
            case "primary":
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.PRIMARY_PACKAGING_TYPE.SHRIMP.TT";
            case "secondary":
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.SECONDARY_PACKAGING_TYPE.SHRIMP.TT";
            case "tertiary":
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.TERTIARY_PACKAGING_TYPE.SHRIMP.TT";
            default:
                return "SUSTELL.STAGES.PROCESSING.PACKAGING.PRIMARY_PACKAGING_TYPE.SHRIMP.TT";
        }
    };

    const getPackagingOptionsByType = (type: PackagingTypes) => {
        const baseOption = {
            text: intl.formatMessage({
                id: "SUSTELL.STAGES.PROCESSING.PACKAGING.EMPTY_PACKAGING",
            }),
            value: "",
        };

        switch (type) {
            case 'primary':
                return [
                    baseOption,
                    ...enumToOptionsArrayWithTranslatedStrings(
                        PrimaryPackagingType,
                        intl,
                        "SUSTELL.TRANSPORT_MODE."
                    ),
                ];
            case 'secondary':
                return [
                    baseOption,
                    ...enumToOptionsArrayWithTranslatedStrings(
                        SecondaryPackagingType,
                        intl,
                        "SUSTELL.TRANSPORT_MODE."
                    ),
                ];
            case 'tertiary':
                return [
                    baseOption,
                    ...enumToOptionsArrayWithTranslatedStrings(
                        TertiaryPackagingType,
                        intl,
                        "SUSTELL.TRANSPORT_MODE."
                    ),
                ];
            default:
                return [baseOption];
        }
    };

    const fieldPrefix = fieldItemPrefix.replace(/\.\d+$/, "");

    const items = useWatch({
        name: fieldPrefix,
        control: fc.control,
    });

    const getAvailableOptions = () => {
        const currOptionType = fc.getValues(`${fieldItemPrefix}.type`);
        const list = getPackagingOptionsByType(packagingType as PackagingTypes);

        if (!Array.isArray(items)) return [];

        const existingTypes = items
            .map((item) => item?.type)
            .filter(Boolean);

        const unavailableValues = unavailableOptions.map(opt => opt.value);

        return list.filter((option) => {
            const isUsed = existingTypes.includes(option.value);
            const isUnavailable = unavailableValues.includes(option.value);
            const isCurrent = option.value === currOptionType;

            return (!isUsed || isCurrent) && !isUnavailable;
        });
    };

    const selectedOutput = useWatch({
        name: `${fieldPrefix.replace(/\.[^.]+$/, "")}.outputId`,
        control: fc.control,
    });

    useEffect(() => {
        if (!selectedOutput) {
            setDisablePackagingTypeField(true);
            fc.setValue(`${fieldItemPrefix}.type`, "");
        }
        else {
            setDisablePackagingTypeField(false);
        }
    }, [selectedOutput]);

    const selectedType = useWatch({
        name: `${fieldItemPrefix}.type`,
        control: fc.control,
    });

    const getUnitByPackaging = (): string => {
        if (selectedType === PrimaryPackagingType.PouchLamination) {
            return lookupValues.units.area.m2.label;
        }

        if (selectedType === TertiaryPackagingType.WoodPallet) {
            return intl.formatMessage({ id: "SUSTELL.STAGES.PROCESSING.PACKAGING.WOOD_PALET.UNIT" });
        }

        return userUOM?.unitOutputMass;
    };

    useEffect(() => {
        if (!selectedType) {
            setDisableAmountField(true);
            fc.setValue(`${fieldItemPrefix}.amount`, "");
        } else {
            setDisableAmountField(false);
        }
    }, [selectedType]);

    return (
        <>
            <input
                ref={fc.register()}
                type="hidden"
                name={`${fieldItemPrefix}.id`}
                value={
                    fc.getValues(`${fieldItemPrefix}.id`) ?
                        fc.getValues(`${fieldItemPrefix}.id`) :
                        uuidv4()
                }
            />
            <ReactHookDsmSelect
                label={intl.formatMessage({
                    id: getPackagingLabelId(packagingType),
                })}
                name={`${fieldItemPrefix}.type`}
                options={getAvailableOptions()}
                disabled={formType === FormType.View || disablePackagingTypeField}
                tooltip={intl.formatMessage({
                    id: getPackagingTooltipId(packagingType),
                })}
                defaultValue={fc.getValues(`${fieldItemPrefix}.type`)}
            />
            <ReactHookDsmInput
                name={`${fieldItemPrefix}.amount`}
                label={`${intl.formatMessage({
                    id: 'SUSTELL.STAGES.PROCESSING.PACKAGING.PACKAGING_AMOUNT',
                })}`}
                type="number"
                adornment={getUnitByPackaging()}
                disabled={formType === FormType.View || disableAmountField || disablePackagingTypeField}
                tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGES.PROCESSING.PACKAGING.PACKAGING_AMOUNT.SHRIMP.TT",
                })}
                required
            />
        </>
    );
};

export default ShrimpPackagingTypeComponent;
